import { clsx, type ClassValue } from 'clsx';
import { API_BASE_URL } from '@/config/apiBaseUrl';

/**
 * Utility function to merge class names
 */
export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

/**
 * Format date using date-fns
 */
export { formatParisDateTime, formatLocalDateTimeForPicker, localPickerValueToIso, parseApiDateTime } from './dateTime';

export function formatDate(date: string | Date, _format: string = 'PP'): string {
  // This will be implemented when date-fns is used
  return new Date(date).toLocaleDateString();
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

/**
 * Get full URL for project logo
 * Logo path from API is relative (/uploads/projects/...)
 * We need to prepend the API base URL (without /api)
 */
export function getProjectLogoUrl(logoPath: string | null | undefined): string | null {
  if (!logoPath) return null;
  
  // If already a full URL, return as is
  if (logoPath.startsWith('http://') || logoPath.startsWith('https://')) {
    return logoPath;
  }
  
  // Get API base URL and remove /api suffix if present
  const apiBaseUrl = API_BASE_URL;
  const baseUrl = apiBaseUrl.replace(/\/api$/, '');
  
  // Ensure logo path starts with /
  const normalizedPath = logoPath.startsWith('/') ? logoPath : `/${logoPath}`;
  
  return `${baseUrl}${normalizedPath}`;
}

/**
 * Get full URL for project map image
 * Path from API is relative (/uploads/projects/maps/...)
 */
export function getProjectMapImageUrl(path: string | null | undefined): string | null {
  if (!path) return null;

  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  const apiBaseUrl = API_BASE_URL;
  const baseUrl = apiBaseUrl.replace(/\/api$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${normalizedPath}`;
}
